import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import PublishIcon from "@mui/icons-material/Publish";
import RejectIcon from "@mui/icons-material/DoNotDisturb";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import viewpaths from "global/constants/ViewPathConstants";
import {
  handleErrorMessage,
  openSuccessNotification,
} from "helpers/methods";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  getAllGeneratedBlogs,
  rejectBlog,
} from "screens/Admin/AdminDashboardServices";
import type { AdminBlogListItem } from "screens/Admin/types";
import generatedBlogsListStyles from "./GeneratedBlogsList.styles";

const formatDate = (d: string) =>
  d ? new Date(d).toLocaleDateString("en-US", { dateStyle: "medium" }) : "—";

const GeneratedBlogsList = () => {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const styles = generatedBlogsListStyles;
  const [blogs, setBlogs] = useState<AdminBlogListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [goToPageValue, setGoToPageValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllGeneratedBlogs(page, rowsPerPage);
      setBlogs(res.blogs ?? []);
      setTotal(res.total ?? 0);
    } catch (e: any) {
      handleErrorMessage(e);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh every 30 seconds when component is in view
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          intervalId = setInterval(load, 30_000);
        } else {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (intervalId) clearInterval(intervalId);
    };
  }, [load]);

  // const handlePublish = async (id: string) => {
  //   setPublishingId(id);
  //   try {
  //     await publishBlog(id, { publish: true });
  //     load();
  //   } catch (e: any) {
  //     handleErrorMessage(e?.message ?? "Failed to publish.");
  //   } finally {
  //     setPublishingId(null);
  //   }
  // };

  const handleEdit = (id: string) => {
    history.push(viewpaths.adminBlogEditViewPath.replace(":id", id));
  };

  const handleReject = async (id: string) => {
    setRejectingId(id);
    try {
      await rejectBlog(id);
      openSuccessNotification("Blog rejected.");
      load();
    } catch (e: any) {
      handleErrorMessage(e);
    } finally {
      setRejectingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleGoToPage = () => {
    const p = parseInt(goToPageValue, 10);
    if (p >= 1 && p <= totalPages) {
      handlePageChange(p);
      setGoToPageValue("");
    }
  };

  const handleRandomPage = () => {
    const randomP = Math.floor(Math.random() * totalPages) + 1;
    handlePageChange(randomP);
  };

  return (
    <Box id="blogs" ref={sectionRef} sx={styles.section}>
      {loading ? (
        <Box sx={styles.empty}>
          <CircularProgress size={48} sx={{ color: "#f8481c" }} />
          <Typography sx={{ mt: 2 }}>Loading blogs...</Typography>
        </Box>
      ) : blogs.length === 0 ? (
        <Box sx={styles.empty}>
          <Typography>No blogs found.</Typography>
        </Box>
      ) : isMobile ? (
        <Grid container spacing={2}>
          {blogs.map((blog, i) => (
            <Grid item xs={12} key={blog.id}>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Box sx={styles.card}>
                  <Typography fontWeight={600} sx={{ mb: 1 }}>
                    {blog.title}
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}
                  >
                    <Chip
                      size="small"
                      label={blog.status}
                      sx={styles.statusChip}
                      color={
                        blog.status === "published"
                          ? "success"
                          : blog.status === "failed"
                            ? "error"
                            : "default"
                      }
                    />
                    <Typography variant="body2" color="text.secondary">
                      {blog.category} · {formatDate(blog.createdAt)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Likes: {blog.likes ?? 0} · Views: {blog.views ?? 0}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(blog.id)}
                    >
                      Edit
                    </Button>
                    {blog.status === "draft" && (
                      <Button
                        size="small"
                        startIcon={<PublishIcon />}
                        // onClick={() => handlePublish(blog.id)}
                        disabled={publishingId === blog.id}
                      >
                        {publishingId === blog.id ? "Publishing…" : "Publish"}
                      </Button>
                    )}
                    <Button
                      size="small"
                      startIcon={
                        rejectingId === blog.id ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <RejectIcon />
                        )
                      }
                      onClick={() => handleReject(blog.id)}
                      disabled={rejectingId === blog.id}
                      color="error"
                      sx={{
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: (t) =>
                            alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.2 : 0.08),
                        },
                      }}
                    >
                      {rejectingId === blog.id ? "Rejecting…" : "Reject"}
                    </Button>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflowX: "auto",
            maxWidth: "100%",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blogs.map((blog) => (
                <TableRow key={blog.id} hover>
                  <TableCell>
                    <Typography fontWeight={500}>{blog.title}</Typography>
                  </TableCell>
                  <TableCell>{blog.key}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(blog.id)}
                      aria-label="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {blog.status === "draft" && (
                      <IconButton
                        size="small"
                        // onClick={() => handlePublish(blog.id)}
                        disabled={publishingId === blog.id}
                        aria-label="Publish"
                      >
                        <PublishIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleReject(blog.id)}
                      disabled={rejectingId === blog.id}
                      color="error"
                      aria-label="Reject"
                      sx={{
                        "&:hover": {
                          bgcolor: (t) =>
                            alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.2 : 0.08),
                        },
                      }}
                    >
                      {rejectingId === blog.id ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <RejectIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination - same style as Blogs.tsx, server-side (page + rowsPerPage in API) */}
      {!loading && blogs.length > 0 && (
        <Box
          sx={{
            mt: 4,
            pt: 4,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: totalPages > 1 ? 3 : 0,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme.palette.text.secondary,
                  whiteSpace: "nowrap",
                }}
              >
                Items per page:
              </Typography>
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                size="small"
                sx={{
                  minWidth: "80px",
                  height: "36px",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha("#1b1b1b", 0.8)
                      : alpha("#ffffff", 0.8),
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  "&:hover": {
                    border: `1px solid ${alpha("#f8481c", 0.3)}`,
                  },
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&.Mui-focused": {
                    border: `1px solid ${alpha("#f8481c", 0.5)}`,
                    boxShadow: `0 0 0 3px ${alpha("#f8481c", 0.1)}`,
                  },
                  "& .MuiSelect-select": {
                    fontSize: "14px",
                    py: 1,
                    color: theme.palette.text.primary,
                  },
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={20}>20</MenuItem>
              </Select>
            </Stack>

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.text.secondary,
                    whiteSpace: "nowrap",
                  }}
                >
                  Go to:
                </Typography>
                <TextField
                  type="number"
                  value={goToPageValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (parseInt(value, 10) >= 1 &&
                        parseInt(value, 10) <= totalPages)
                    ) {
                      setGoToPageValue(value);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") handleGoToPage();
                  }}
                  placeholder={`1-${totalPages}`}
                  size="small"
                  inputProps={{
                    min: 1,
                    max: totalPages,
                    style: { textAlign: "center", width: "60px" },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha("#1b1b1b", 0.8)
                          : alpha("#ffffff", 0.8),
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      width: "80px",
                      "&:hover": {
                        border: `1px solid ${alpha("#f8481c", 0.3)}`,
                      },
                      "&.Mui-focused": {
                        border: `1px solid ${alpha("#f8481c", 0.5)}`,
                        boxShadow: `0 0 0 3px ${alpha("#f8481c", 0.1)}`,
                      },
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      py: 1,
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={handleGoToPage}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      background: alpha("#f8481c", 0.1),
                      border: `1px solid ${alpha("#f8481c", 0.3)}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: alpha("#f8481c", 0.2),
                        border: `1px solid ${alpha("#f8481c", 0.5)}`,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#f8481c",
                      }}
                    >
                      Go
                    </Typography>
                  </Box>
                </motion.div>
              </Stack>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={handleRandomPage}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    background: alpha("#f8481c", 0.1),
                    border: `1px solid ${alpha("#f8481c", 0.3)}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: alpha("#f8481c", 0.2),
                      border: `1px solid ${alpha("#f8481c", 0.5)}`,
                    },
                  }}
                >
                  <Typography
                    sx={{ fontSize: "13px", fontWeight: 600, color: "#f8481c" }}
                  >
                    Random
                  </Typography>
                </Box>
              </motion.div>
            </Stack>
          </Box>

          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
              }}
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => handlePageChange(1)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    cursor: page === 1 ? "not-allowed" : "pointer",
                    background:
                      page === 1
                        ? alpha(theme.palette.divider, 0.1)
                        : theme.palette.mode === "dark"
                          ? alpha("#1b1b1b", 0.8)
                          : alpha("#ffffff", 0.8),
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    transition: "all 0.3s ease",
                    opacity: page === 1 ? 0.5 : 1,
                    "&:hover": {
                      background:
                        page === 1 ? undefined : alpha("#f8481c", 0.1),
                      border:
                        page === 1
                          ? undefined
                          : `1px solid ${alpha("#f8481c", 0.3)}`,
                    },
                  }}
                >
                  <FirstPageIcon
                    sx={{
                      fontSize: 20,
                      color:
                        page === 1
                          ? theme.palette.text.disabled
                          : theme.palette.text.primary,
                    }}
                  />
                </Box>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => handlePageChange(page - 1)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    cursor: page === 1 ? "not-allowed" : "pointer",
                    background:
                      page === 1
                        ? alpha(theme.palette.divider, 0.1)
                        : theme.palette.mode === "dark"
                          ? alpha("#1b1b1b", 0.8)
                          : alpha("#ffffff", 0.8),
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    transition: "all 0.3s ease",
                    opacity: page === 1 ? 0.5 : 1,
                    "&:hover": {
                      background:
                        page === 1 ? undefined : alpha("#f8481c", 0.1),
                      border:
                        page === 1
                          ? undefined
                          : `1px solid ${alpha("#f8481c", 0.3)}`,
                    },
                  }}
                >
                  <ChevronLeftIcon
                    sx={{
                      fontSize: 20,
                      color:
                        page === 1
                          ? theme.palette.text.disabled
                          : theme.palette.text.primary,
                    }}
                  />
                </Box>
              </motion.div>

              <Stack direction="row" spacing={0.5}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (p) => {
                    const showPage =
                      p === 1 ||
                      p === totalPages ||
                      (p >= page - 1 && p <= page + 1);
                    if (!showPage) {
                      if (p === page - 2 || p === page + 2) {
                        return (
                          <Typography
                            key={p}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 40,
                              height: 40,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            ...
                          </Typography>
                        );
                      }
                      return null;
                    }
                    return (
                      <motion.div
                        key={p}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={() => handlePageChange(p)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 40,
                            height: 40,
                            px: 1.5,
                            cursor: "pointer",
                            background:
                              page === p
                                ? `linear-gradient(135deg, ${alpha("#f8481c", 0.2)} 0%, ${alpha("#f8481c", 0.15)} 100%)`
                                : theme.palette.mode === "dark"
                                  ? alpha("#1b1b1b", 0.8)
                                  : alpha("#ffffff", 0.8),
                            border: `1px solid ${
                              page === p
                                ? alpha("#f8481c", 0.5)
                                : alpha(theme.palette.divider, 0.2)
                            }`,
                            borderLeft:
                              page === p
                                ? `3px solid ${alpha("#f8481c", 0.8)}`
                                : undefined,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background:
                                page === p ? undefined : alpha("#f8481c", 0.1),
                              border:
                                page === p
                                  ? undefined
                                  : `1px solid ${alpha("#f8481c", 0.3)}`,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: page === p ? 700 : 500,
                              color:
                                page === p
                                  ? "#f8481c"
                                  : theme.palette.text.primary,
                            }}
                          >
                            {p}
                          </Typography>
                        </Box>
                      </motion.div>
                    );
                  },
                )}
              </Stack>

              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => handlePageChange(page + 1)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    cursor: page === totalPages ? "not-allowed" : "pointer",
                    background:
                      page === totalPages
                        ? alpha(theme.palette.divider, 0.1)
                        : theme.palette.mode === "dark"
                          ? alpha("#1b1b1b", 0.8)
                          : alpha("#ffffff", 0.8),
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    transition: "all 0.3s ease",
                    opacity: page === totalPages ? 0.5 : 1,
                    "&:hover": {
                      background:
                        page === totalPages ? undefined : alpha("#f8481c", 0.1),
                      border:
                        page === totalPages
                          ? undefined
                          : `1px solid ${alpha("#f8481c", 0.3)}`,
                    },
                  }}
                >
                  <ChevronRightIcon
                    sx={{
                      fontSize: 20,
                      color:
                        page === totalPages
                          ? theme.palette.text.disabled
                          : theme.palette.text.primary,
                    }}
                  />
                </Box>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => handlePageChange(totalPages)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    cursor: page === totalPages ? "not-allowed" : "pointer",
                    background:
                      page === totalPages
                        ? alpha(theme.palette.divider, 0.1)
                        : theme.palette.mode === "dark"
                          ? alpha("#1b1b1b", 0.8)
                          : alpha("#ffffff", 0.8),
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    transition: "all 0.3s ease",
                    opacity: page === totalPages ? 0.5 : 1,
                    "&:hover": {
                      background:
                        page === totalPages ? undefined : alpha("#f8481c", 0.1),
                      border:
                        page === totalPages
                          ? undefined
                          : `1px solid ${alpha("#f8481c", 0.3)}`,
                    },
                  }}
                >
                  <LastPageIcon
                    sx={{
                      fontSize: 20,
                      color:
                        page === totalPages
                          ? theme.palette.text.disabled
                          : theme.palette.text.primary,
                    }}
                  />
                </Box>
              </motion.div>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default GeneratedBlogsList;
